<template>
  <div class="cookie-settings-modal">
    <div class="modal-header">
      <h2>Cookie Settings</h2>
    </div>

    <div class="modal-content">
      <div class="tabs-container">
        <div class="tabs-list">
          <div
            v-for="tab in tabs"
            :key="tab.name"
            :class="['tab-item', { active: activeTab === tab.name }]"
            @click="activeTab = tab.name"
          >
            {{ tab.label }}
          </div>
        </div>

        <div class="content-container">
          <div class="content-header">
            <h3>{{ getCurrentTabLabel() }}</h3>
          </div>

          <div class="content-body">
            <div v-if="activeTab === 'aboutCookies'" class="tab-content">
              <p>
                This site uses cookies. We use cookies mainly to improve and
                analyze your experience on our websites and for marketing
                purposes. Because we respect your right to privacy, you can
                choose not to allow some types of cookies. Click on the
                different category headings to find out more and change your
                default settings. Blocking some types of cookies may negatively
                impact your experience on the site and limit the services we are
                able to provide.
              </p>
              <p>We use different types of cookies for various purposes:</p>
              <ul>
                <li>
                  <strong>Necessary cookies:</strong> Essential for the website
                  to function properly
                </li>
                <li>
                  <strong>Analytics cookies:</strong> Help us understand how
                  visitors use our website
                </li>
                <li>
                  <strong>Advertising cookies:</strong> Used to deliver relevant
                  advertisements
                </li>
                <li>
                  <strong>Functional cookies:</strong> Enable enhanced
                  functionality and personalization
                </li>
              </ul>
            </div>

            <div v-if="activeTab === 'Necessary'" class="tab-content">
              <p>
                These cookies are necessary for the website to function and
                cannot be switched off in our systems. They are usually only set
                in response to actions made by you which amount to a request for
                services, such as setting your privacy preferences, logging in,
                or filling in forms.
              </p>
              <div class="cookie-toggle">
                <label class="toggle-label">
                  <span>Necessary Cookies</span>
                  <input
                    type="checkbox"
                    v-model="preferences.necessary"
                    disabled
                    checked
                  />
                  <span class="toggle-slider"></span>
                </label>
                <p class="cookie-description">
                  Always enabled - required for basic site functionality
                </p>
              </div>
            </div>

            <div v-if="activeTab === 'Analytics'" class="tab-content">
              <p>
                These cookies help us to understand how visitors engage with the
                website. We may use a set of cookies to collect information and
                report site usage statistics. In addition to reporting site
                usage statistics, data collected may also be used, together with
                some of the advertising cookies described, to help show more
                relevant ads across the web and to measure interactions with the
                ads we show.
              </p>
              <div class="cookie-toggle">
                <label class="toggle-label">
                  <span>Analytics Cookies</span>
                  <input type="checkbox" v-model="preferences.analytics" />
                  <span class="toggle-slider"></span>
                </label>
                <p class="cookie-description">
                  Help us improve our website by collecting usage statistics
                </p>
              </div>
            </div>

            <div v-if="activeTab === 'Advertising'" class="tab-content">
              <p>
                We use cookies to make our ads more engaging and valuable to
                site visitors. Some common applications of cookies are to select
                advertising based on what’s relevant to a user; to improve
                reporting on ad campaign performance; and to avoid showing ads
                the user has already seen.
              </p>
              <div class="cookie-toggle">
                <label class="toggle-label">
                  <span>Advertising Cookies</span>
                  <input type="checkbox" v-model="preferences.advertising" />
                  <span class="toggle-slider"></span>
                </label>
                <p class="cookie-description">
                  Used to show you relevant advertisements
                </p>
              </div>
            </div>

            <div v-if="activeTab === 'Functionality'" class="tab-content">
              <p>
                We use a set of cookies that are optional for the website to
                function. They are usually only set in response to information
                provided to the website to personalize and optimize your
                experience as well as remember your chat history.
              </p>
              <div class="cookie-toggle">
                <label class="toggle-label">
                  <span>Functional Cookies</span>
                  <input type="checkbox" v-model="preferences.functional" />
                  <span class="toggle-slider"></span>
                </label>
                <p class="cookie-description">
                  Remember your preferences and provide enhanced features
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn btn-primary" @click="saveSettings">
        Save Settings
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'CookieSettings',
  data() {
    return {
      activeTab: 'aboutCookies',
      preferences: {
        necessary: true,
        analytics: false,
        advertising: false,
        functional: false
      },
      tabs: [
        {
          name: 'aboutCookies',
          label: 'About Cookies'
        },
        {
          name: 'Necessary',
          label: 'Necessary'
        },
        {
          name: 'Analytics',
          label: 'Analytics'
        },
        {
          name: 'Advertising',
          label: 'Advertising'
        },
        {
          name: 'Functionality',
          label: 'Functionality'
        }
      ]
    }
  },
  methods: {
    ...mapActions('cookies', ['saveCustomPreferences']),
    saveSettings() {
      this.saveCustomPreferences(this.preferences)
      this.$modal.close()
    },
    getCurrentTabLabel() {
      const currentTab = this.tabs.find(tab => tab.name === this.activeTab)
      return currentTab ? currentTab.label : ''
    }
  },
  mounted() {
    const currentPreferences = this.$store.getters['cookies/getPreferences']
    if (currentPreferences) {
      this.preferences = { ...currentPreferences }
    }
  }
}
</script>

<style scoped>
.cookie-settings-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 800px;
  width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.modal-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #111827;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #374151;
}

.modal-content {
  flex: 1;
  overflow: hidden;
  width: 100%;
}

.tabs-container {
  display: flex;
  height: 100%;
}

.tabs-list {
  width: 200px;
  background: #f3f4f6;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
}

.tab-item {
  padding: 16px 20px;
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
  transition: all 0.2s;
  font-weight: 500;
  color: #374151;
}

.tab-item:hover {
  background: #e5e7eb;
  color: #111827;
}

.tab-item.active {
  background: white;
  color: #013366;
  border-right: 3px solid #013366;
  font-weight: 600;
}

.content-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.content-header {
  padding: 24px 32px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
}

.content-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.content-body {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

.tab-content {
  line-height: 1.6;
  color: #374151;
}

.tab-content p {
  margin-bottom: 16px;
}

.tab-content ul {
  margin: 16px 0;
  padding-left: 20px;
}

.tab-content li {
  margin-bottom: 8px;
}

.cookie-toggle {
  margin: 24px 0;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.toggle-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: 500;
  color: #111827;
}

.toggle-label input[type='checkbox'] {
  display: none;
}

.toggle-slider {
  position: relative;
  width: 50px;
  height: 24px;
  background: #d1d5db;
  border-radius: 12px;
  transition: background 0.2s;
}

.toggle-slider:before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-label input[type='checkbox']:checked + .toggle-slider {
  background: #013366;
}

.toggle-label input[type='checkbox']:checked + .toggle-slider:before {
  transform: translateX(26px);
}

.toggle-label input[type='checkbox']:disabled + .toggle-slider {
  background: #9ca3af;
  opacity: 0.6;
}

.cookie-description {
  margin: 8px 0 0 0;
  font-size: 14px;
  color: #6b7280;
  font-style: italic;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px 32px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.btn {
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 14px;
}

.btn-primary {
  background: #013366;
  color: white;
}

.btn-primary:hover {
  background: #012a4a;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

@media (max-width: 768px) {
  .cookie-settings-modal {
    width: 95vw;
    max-height: 90vh;
  }

  .tabs-container {
    flex-direction: column;
  }

  .tabs-list {
    width: 100%;
    max-height: 200px;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .tab-item.active {
    border-right: none;
    border-bottom: 3px solid #013366;
  }
}
</style>
